import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { api, API } from "@/lib/api";
import {
  msalInstance,
  msalReady,
  isMsalConfigured,
  isInsideMsalPopup,
  isInsideIframe,
} from "@/lib/msalInstance";
import { loginRequest } from "@/lib/msalConfig";

const AuthContext = createContext(null);

/**
 * AuthProvider supports two modes:
 *   1. MSAL (production / configured)   - uses Microsoft Entra ID sign-in
 *   2. Dev header (preview / fallback)  - X-User-Email + ?user= query param
 *
 * Mode is decided per-render by isMsalConfigured(). The `useMsal()` hook is
 * always called (rules of hooks) but its state is only consulted when MSAL
 * is active.
 */
export function AuthProvider({ children }) {
  const msal = useMsal();
  // MSAL is only "usefully" active when it's configured AND the app is running
  // top-level. Inside an iframe (e.g. Emergent preview) Microsoft's login page
  // refuses to render, so we transparently fall back to the dev sign-in flow.
  // Testing-agent escape hatch: an explicit ?user=<email> query param or a
  // previously-persisted foc.userEmail forces dev-mode even top-level, so
  // stand-alone Playwright sessions can authenticate without MSAL redirects.
  const _qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const _devOverride = !!(_qs?.get("user")) || (typeof window !== "undefined" && !!window.localStorage?.getItem("foc.userEmail"));
  const msalActive = isMsalConfigured() && !isInsideIframe() && !_devOverride;

  // ------- Dev-mode state -------
  const [devEmail, setDevEmail] = useState(() => {
    if (msalActive) return "";
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = qs.get("user");
    if (fromQuery) {
      localStorage.setItem("foc.userEmail", fromQuery);
      window.history.replaceState({}, "", window.location.pathname);
      return fromQuery;
    }
    return localStorage.getItem("foc.userEmail") || "";
  });

  // ------- Shared state -------
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | signed_in | signed_out | denied
  const [ssoTried, setSsoTried] = useState(false);

  // The "current email" used for /auth/me lookup, source depends on mode.
  const signedInEmail = msalActive
    ? msal.accounts[0]?.username || ""
    : devEmail;

  // Silent SSO attempt on first mount when MSAL is configured but no account
  // is yet known. Picks up an existing Microsoft session (Outlook/Teams) so
  // staff don't need to re-enter credentials. Skipped when:
  //  - we're inside an MSAL popup window (it will close itself), or
  //  - a redirect response is being processed (handleRedirectPromise will set
  //    the account).
  useEffect(() => {
    if (!msalActive || ssoTried) return;
    if (isInsideMsalPopup()) {
      setSsoTried(true);
      return;
    }
    if (msal.accounts.length > 0) {
      setSsoTried(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await msalReady; // wait for handleRedirectPromise to complete first
        if (cancelled) return;
        if (msalInstance.getActiveAccount()) {
          // redirect handler already set us up
          return;
        }
        const resp = await msalInstance.ssoSilent(loginRequest);
        if (!cancelled && resp?.account) {
          msalInstance.setActiveAccount(resp.account);
        }
      } catch (_) {
        // No active MS session, third-party cookies blocked, or iframe
        // restrictions. The Sign in with Microsoft button is shown.
      } finally {
        if (!cancelled) setSsoTried(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [msalActive, msal.accounts.length, ssoTried]);

  const fetchMe = useCallback(async () => {
    if (msalActive) {
      // MSAL handles auth headers via api.js interceptor; no header to set here.
      if (msal.accounts.length === 0) {
        // While the initial silent-SSO attempt is in flight, stay in
        // "loading" so the SignIn page does not flash on top of an
        // imminent silent sign-in.
        if (!ssoTried) {
          setStatus("loading");
          return;
        }
        setStatus("signed_out");
        setUser(null);
        return;
      }
      try {
        const res = await api.get(`/auth/me`);
        setUser(res.data);
        setStatus("signed_in");
      } catch (e) {
        if (e?.response?.status === 403) setStatus("denied");
        else setStatus("signed_out");
        setUser(null);
      }
      return;
    }

    // Dev-mode path
    if (!devEmail) {
      setStatus("signed_out");
      setUser(null);
      return;
    }
    api.defaults.headers.common["X-User-Email"] = devEmail;
    axios.defaults.headers.common["X-User-Email"] = devEmail;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { "X-User-Email": devEmail },
      });
      setUser(res.data);
      setStatus("signed_in");
    } catch (e) {
      if (e?.response?.status === 403) setStatus("denied");
      else setStatus("signed_out");
      setUser(null);
    }
  }, [msalActive, msal.accounts, devEmail, ssoTried]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Dev-mode header injection
  useEffect(() => {
    if (msalActive) return;
    if (devEmail) {
      api.defaults.headers.common["X-User-Email"] = devEmail;
      axios.defaults.headers.common["X-User-Email"] = devEmail;
    } else {
      delete api.defaults.headers.common["X-User-Email"];
      delete axios.defaults.headers.common["X-User-Email"];
    }
  }, [msalActive, devEmail]);

  const signIn = async (newEmailOrUndefined) => {
    // ARC-014 · P0 fix (2026-08-05): the Microsoft button MUST always
    // trigger the MSAL redirect flow when MSAL is configured, even if
    // this session is currently in dev-override mode (e.g. a stale
    // `foc.userEmail` left behind by an earlier preview session on the
    // same host). Without this, clicking "Sign in with Microsoft" is a
    // silent no-op on production for any user whose browser has that
    // stale localStorage entry.
    if (msalActive || (isMsalConfigured() && !isInsideIframe() && !newEmailOrUndefined)) {
      // Always use redirect flow. Popup is unreliable in iframed previews
      // (block_nested_popups) and inside corporate browsers with strict
      // pop-up policies. Redirect flow works universally.
      await msalReady;
      await msalInstance.loginRedirect(loginRequest);
      return;
    }
    // Dev-mode
    if (newEmailOrUndefined) {
      localStorage.setItem("foc.userEmail", newEmailOrUndefined);
      setDevEmail(newEmailOrUndefined);
    }
  };

  const signOut = async () => {
    if (msalActive) {
      // FOC-only sign-out per requirement: clear local cache, keep Microsoft
      // session active so Outlook / Teams stays signed in.
      try {
        await msalInstance.clearCache();
      } catch (_) {
        /* no-op */
      }
      msalInstance.setActiveAccount(null);
      setUser(null);
      setStatus("signed_out");
      return;
    }
    // Dev-mode
    localStorage.removeItem("foc.userEmail");
    setDevEmail("");
    setUser(null);
    setStatus("signed_out");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        email: signedInEmail,
        status,
        signIn,
        signOut,
        msalActive,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };

export const hasPermission = (role, permission) => {
  const map = {
    Administrator: ["all", "users", "customers", "drivers", "products", "settings", "orders.create", "orders.edit", "orders.delete", "orders.view_all", "reports", "notifications"],
    Owner: ["orders.view_all", "orders.create", "reports", "notifications", "dashboards", "customers.view", "drivers.view"],
    "Office Staff": ["orders.view_all", "orders.create", "orders.edit", "customers.view", "drivers.assign", "notifications"],
    Driver: ["orders.view_assigned", "orders.update_status"],
  };
  return (map[role] || []).includes("all") || (map[role] || []).includes(permission);
};
